/* 分析工具 — 每日重點資訊（Daily Briefing）
 *
 *  list–detail：左側可搜尋／分類篩選新聞清單，右側呈現新聞摘要 + 原文連結。
 *
 *  資料來源：Portal 後端 proxy `GET /api/news`（→ 內網 tw-news-board 服務）。
 *    回傳 list[NewsItem]，每筆：id / source / title / url / summary /
 *    category / published_at / fetched_at。
 *  上游本身無全文，故詳情頁呈現摘要並導向「查看原文」。
 */

// 為任意分類名穩定產生一個錨定色（上游分類多達數十種，不逐一列舉）
const DB_PALETTE = ['#0B5F5A', '#3A5A8C', '#A56A1F', '#7A4A6E', '#4A6B7A', '#5E6B3A', '#9B5330', '#2F6E4F', '#6E4A4A', '#41607A'];
function DB_catColor(c) {
  let h = 0;
  for (let i = 0; i < (c || '').length; i++) h = (h * 31 + c.charCodeAt(i)) >>> 0;
  return DB_PALETTE[h % DB_PALETTE.length];
}
function DB_fmtTime(dt) {
  return dt ? dt.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--';
}
function DB_fmtDate(dt) {
  return dt ? dt.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }) : '';
}
// 60 分鐘內視為新進
function DB_isRecent(dt) { return !!dt && (Date.now() - dt.getTime()) <= 60 * 60 * 1000; }

/* Expressive（新版外殼）專屬：NEW 標籤 patch（mockup .new：800 字重、圓角 6、≥10.5px）。
 * 函式化 → render 時才讀 GxT（不在模組頂層抄值）。 */
function DB_GX_NEW() {
  return { fontSize: 11, fontWeight: 800, letterSpacing: 0, borderRadius: GxT.radius.chipSm, padding: '3px 6px', lineHeight: 1 };
}
/* 手機：清單＋詳情兩欄改為上下堆疊（Bento 恆為 1280 縮放，不受影響）。No-op under Bento. */
GxCore.injectCss('gx-css-p-dailybrief', String.raw`
@media (max-width:899px){
html[data-ui-shell="expressive"] .gx-db-split{grid-template-columns:minmax(0,1fr)!important}
html[data-ui-shell="expressive"] .gx-db-detail{padding:22px 20px 26px!important}
}
`);

function DailyBriefPage() {
  const toast = useToast();
  const [items, setItems] = React.useState(null);   // null = 尚未載入
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [cat, setCat] = React.useState('全部');
  const [selId, setSelId] = React.useState(null);
  const [searchFocus, setSearchFocus] = React.useState(false);
  const [sortAsc, setSortAsc] = React.useState(false);   // false = 最新在前（預設）

  const refresh = React.useCallback(() => {
    setLoading(true);
    api('GET', '/news?limit=100')
      .then(raw => {
        const list = Array.isArray(raw) ? raw : (raw && raw.items) || [];
        setItems(list.map(n => ({
          _id: n.id,
          category: n.category || '未分類',
          title: n.title || '(無標題)',
          summary: n.summary || '',
          source: n.source || '',
          url: n.url || '#',
          dt: n.published_at ? new Date(n.published_at) : null,
          fetched: n.fetched_at ? new Date(n.fetched_at) : null,
        })));
      })
      .catch(e => { toast((e && e.detail) || '新聞載入失敗', 'error'); setItems([]); })
      .finally(() => setLoading(false));
  }, [toast]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const all = items || [];
  const catCount = all.reduce((m, i) => { m[i.category] = (m[i.category] || 0) + 1; return m; }, {});
  const categories = ['全部', ...Array.from(new Set(all.map(i => i.category))).sort()];
  const latestDt = all.reduce((m, x) => (x.dt && (!m || x.dt > m) ? x.dt : m), null);
  const updatedDt = all.reduce((m, x) => (x.fetched && (!m || x.fetched > m) ? x.fetched : m), null);

  const query = q.trim().toLowerCase();
  const filtered = all
    .filter(i => (cat === '全部' || i.category === cat) &&
      (!query || (i.title + i.summary + i.source + i.category).toLowerCase().includes(query)))
    .sort((a, b) => {
      const ta = a.dt ? a.dt.getTime() : 0, tb = b.dt ? b.dt.getTime() : 0;
      return sortAsc ? ta - tb : tb - ta;
    });

  // 選取：目前選取被濾掉 → 退回第一筆（不另用 effect，避免迴圈）
  const effSel = filtered.some(i => i._id === selId) ? selId : (filtered[0] ? filtered[0]._id : null);
  const selected = all.find(i => i._id === effSel) || null;

  return (
    <>
      <PageHeader
        eyebrow="分析工具 · Daily Briefing"
        title="市場新聞"
        italic={latestDt ? DB_fmtDate(latestDt) : ''}
        action={
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {updatedDt && (
              <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                資料更新 {DB_fmtTime(updatedDt)}
              </span>
            )}
            <Button variant="ghost" size="sm" icon="refresh" onClick={refresh}>重新整理</Button>
          </span>
        }
      />

      {/* 工具列：搜尋 + 分類下拉 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 400 }}>
          <span style={{
            position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
            color: searchFocus ? HR.accent : HR.inkFaint, display: 'flex', pointerEvents: 'none', transition: 'color 120ms',
          }}>
            <Icon name="search" size={15} />
          </span>
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)}
            onFocus={() => setSearchFocus(true)} onBlur={() => setSearchFocus(false)}
            placeholder="搜尋標題、來源…" aria-label="搜尋市場新聞"
            style={inputStyle(uiStyle({
              paddingLeft: 34, paddingRight: q ? 32 : 11,
              borderColor: searchFocus ? HR.accent : HR.rule,
              boxShadow: searchFocus ? `0 0 0 3px ${GxCore.alpha(HR.accent, '22')}` : 'none',
              transition: 'border-color 120ms, box-shadow 120ms',
            }, {
              // Expressive outlined field (= inputStyle under GX); the focus ring itself comes from gx-css-pages
              borderColor: searchFocus ? GxT.color.p : GxT.color.ol,
              boxShadow: 'none',
            }))}
          />
          {q && (
            <button
              onClick={() => setQ('')} aria-label="清除搜尋"
              style={{
                position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                border: 'none', background: 'none', cursor: 'pointer', color: HR.inkFaint,
                display: 'flex', padding: 3, borderRadius: 4,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = HR.ink; }}
              onMouseLeave={e => { e.currentTarget.style.color = HR.inkFaint; }}
            >
              <Icon name="x" size={13} />
            </button>
          )}
        </div>
        <div style={{ flex: '0 0 auto', width: 176 }}>
          <Select
            value={cat} allowEmpty={false}
            onChange={v => setCat(v || '全部')}
            options={categories.map(c => ({
              value: c,
              label: c === '全部' ? `全部分類 (${all.length})` : `${c} (${catCount[c] || 0})`,
            }))}
          />
        </div>
      </div>

      <div className={GxCore.cls('rwd-stack', 'gx-db-split')} style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.4fr', gap: 18, alignItems: 'start' }}>
        {/* 左：清單 */}
        <Card
          padding="0" eyebrow="News" title="新聞清單"
          italic={loading ? '載入中…' : `${filtered.length} 則`}
          right={
            <Button
              variant="ghost" size="sm" icon="clock"
              onClick={() => setSortAsc(s => !s)}
              title="切換時間排序"
            >
              {sortAsc ? '最舊在前' : '最新在前'}
            </Button>
          }
        >
          {loading ? (
            <SkeletonTable rows={7} cols={['1fr', 70]} header={false} />
          ) : filtered.length === 0 ? (
            <EmptyState icon="search" title="找不到符合的新聞" hint="試試其他關鍵字或切換分類。" />
          ) : (
            <div style={{ maxHeight: 660, overflowY: 'auto' }}>
              {filtered.map(i => {
                const active = i._id === effSel;
                const color = DB_catColor(i.category);
                return (
                  <div
                    key={i._id} onClick={() => setSelId(i._id)}
                    style={uiStyle({
                      padding: '13px 18px', cursor: 'pointer',
                      borderTop: `1px solid ${HR.ruleSoft}`,
                      background: active ? HR.surfaceAlt : 'transparent',
                      borderLeft: `3px solid ${active ? color : 'transparent'}`,
                    }, { padding: '14px 20px 14px 17px', borderTop: `1px solid ${GxT.color.s3}`, background: active ? GxT.color.s2 : 'transparent' })}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{DB_fmtTime(i.dt)}</span>
                      {DB_isRecent(i.dt) && (
                        <span style={uiStyle({ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: '#fff', background: HR.accent, borderRadius: 3, padding: '2px 5px' }, DB_GX_NEW())}>NEW</span>
                      )}
                      <span style={{ fontSize: FontSizes.trading_lg, fontWeight: 700, color }}>{i.category}</span>
                    </div>
                    <div style={uiStyle({
                      fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500, color: HR.ink, lineHeight: 1.4,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }, { fontSize: 15, fontWeight: active ? 700 : 600 })}>{i.title}</div>
                    <div style={{ marginTop: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{i.source}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* 右：新聞詳情 */}
        <div style={uiStyle({ background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6, minHeight: 360 },
          { border: 'none', borderRadius: GxT.radius.card, minWidth: 0 })}>
          {loading ? (
            <div style={{ padding: '32px 36px' }}><SkeletonTable rows={6} cols={['1fr']} header={false} /></div>
          ) : !selected ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
              從左邊選一則新聞檢視內容
            </div>
          ) : (
            <DailyBriefDetail item={selected} />
          )}
        </div>
      </div>
    </>
  );
}

function DailyBriefDetail({ item }) {
  const color = DB_catColor(item.category);
  return (
    <article className={GxCore.cls(undefined, 'gx-db-detail')} style={{ padding: '32px 40px 40px', maxWidth: 988, margin: '0 auto' }}>
      <div style={uiStyle({ fontSize: FontSizes.xs, fontWeight: 700, letterSpacing: '0.04em', color, marginBottom: 12 },
        { letterSpacing: 0 })}>
        ● {item.category}
      </div>
      <h2 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, lineHeight: 1.3, fontWeight: 700, color: HR.ink, letterSpacing: '-0.01em' },
        { fontWeight: GxT.weight.display, lineHeight: 1.22, letterSpacing: '-0.02em' })}>
        {item.title}
      </h2>
      <div style={{
        marginTop: 14, paddingBottom: 16, borderBottom: `1px solid ${HR.rule}`,
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        fontFamily: HR.mono, fontSize: FontSizes.trading_xl, color: HR.inkSoft,
      }}>
        <span>{item.source}</span><span style={{ color: HR.rule }}>·</span>
        <span>{DB_fmtTime(item.dt)}</span><span style={{ color: HR.rule }}>·</span>
        <span>{DB_fmtDate(item.dt)}</span>
        {DB_isRecent(item.dt) && <span style={uiStyle({ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: '#fff', background: HR.accent, borderRadius: 3, padding: '2px 5px' }, DB_GX_NEW())}>NEW</span>}
      </div>
      <p style={{ fontSize: FontSizes.md, lineHeight: 1.85, color: HR.ink, marginTop: 22 }}>
        {item.summary || '（此則無摘要，請點下方查看原文。）'}
      </p>
      <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${HR.rule}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <Button variant="primary" size="sm" icon="ext">查看原文</Button>
        </a>
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>完整內容請見原始來源</span>
      </div>
    </article>
  );
}
